/* eslint-disable react/no-access-state-in-setstate */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/no-this-in-sfc */

import classnames from 'classnames';
import * as React from 'react';
import Collapsible from 'react-collapsible';
import { connect } from 'react-redux';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { Dispatch } from 'redux';
import { change, FieldArray, FormSection } from 'redux-form';
import { AudioNormalizationOrder, DEFAULT_AUDIO_NORMALIZATION_ORDER } from 'stageclip-common';

import { createClearSyncErrorsAction } from '../../actions/errorActions';
import { FormId } from '../../constants/formId';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import {
  BaseDispatchProps,
  baseMapDispatchToProps,
  baseMapStateToProps,
  BaseStateProps,
  ComponentBase,
} from '../componentBase';
import { AppSelectField } from '../form/appSelectField';
import { AppTextField } from '../form/appTextField';
import EditableFieldArray from '../form/editableFieldArray';
import {
  azureEndpointValidator,
  hostnameOnlyValidator,
  maxValue,
  minValue,
  numberFormat,
  required,
} from '../form/formValidations';

import styles from './clipConfiguration.module.scss';

interface ClipConfigurationCustomStyles {
  container?: any;
  tabConfig?: any;
}

interface ClipConfigurationOwnProps {
  formId: FormId;
  readonly?: boolean;
  customStyles?: ClipConfigurationCustomStyles;
}

interface ClipConfigurationStateProps extends BaseStateProps {
  formValues: any;
  clipConfigurationFormErrors: any;
}

interface ClipConfigurationDispatchProps extends BaseDispatchProps {
  changeFieldValue: (field: string, value: any) => void;
  clearSyncErrors: () => void;
}

interface ClipConfigurationState {
  overlaysSelectedIndex: number;
  creditsOverlaysSelectedIndex: number;
  interstitialsSelectedIndex: number;
}

class ClipConfigurationForm extends ComponentBase<
  ClipConfigurationOwnProps,
  ClipConfigurationStateProps,
  ClipConfigurationDispatchProps,
  ClipConfigurationState
> {
  private static renderAudioFields(readonly = false) {
    return (
      <FormSection name="audio">
        <AppTextField name="source" label="Source" validate={required} disabled={readonly} />
        <AppTextField
          name="volume"
          label="Volume"
          validate={[required, numberFormat]}
          type="number"
          disabled={readonly}
        />
      </FormSection>
    );
  }

  private static renderBlobStorageFields(readonly = false) {
    return (
      <FormSection name="blobStorage">
        <AppTextField name="blobContainer" label="Blob container" validate={required} disabled={readonly} />
        <AppTextField name="blobPath" label="Blob path" validate={required} disabled={readonly} />
        <AppTextField
          name="generatorConnectionString"
          label="Generator connection string"
          validate={required}
          disabled={readonly}
        />
        <AppTextField
          name="sourceVideoConnectionString"
          label="Source video connection string"
          validate={required}
          disabled={readonly}
        />
        <AppTextField
          name="azureCdnEndpoint"
          label="Azure CDN endpoint"
          validate={[hostnameOnlyValidator, azureEndpointValidator]}
          disabled={readonly}
        />
      </FormSection>
    );
  }

  private static renderClipFields(readonly = false) {
    return (
      <FormSection name="clip">
        <AppTextField
          name="width"
          label="Width"
          validate={[numberFormat, required]}
          type="number"
          disabled={readonly}
        />
        <AppTextField
          name="height"
          label="Height"
          validate={[numberFormat, required]}
          type="number"
          disabled={readonly}
        />
        <AppTextField name="font" label="Font" validate={required} disabled={readonly} />
        <AppTextField
          name="fadeLength"
          label="Fade length"
          validate={[numberFormat, required]}
          type="number"
          disabled={readonly}
        />
        <AppTextField name="volume" label="Volume" validate={required} type="number" disabled={readonly} />
        <AppTextField name="videoBitRate" label="Video Bit Rate" disabled={readonly} />
        <AppTextField name="audioBitRate" label="Audio Bit Rate" disabled={readonly} />
        <AppTextField name="bufferSize" label="Buffer Size" disabled={readonly} />
        <AppTextField
          name="audioVideoOffset"
          label="Audio - Video offset (seconds)"
          validate={[numberFormat]}
          type="number"
          disabled={readonly}
        />
        <AppTextField
          name="audioVideoDifferenceThreshold"
          label="Audio - Video difference threshold (seconds)"
          validate={[numberFormat]}
          type="number"
          disabled={readonly}
        />
        <AppTextField
          name="musicVolumeWhenDucking"
          label="Music volume when ducking"
          validate={[numberFormat, maxValue(1), minValue(0)]}
          type="number"
          disabled={readonly}
        />
        <AppTextField
          name="slowMotionVideoIncludedClipLengthThreshold"
          label="Include slow motion video if clip is less than (seconds)"
          validate={[numberFormat, minValue(0)]}
          type="number"
          disabled={readonly}
        />
      </FormSection>
    );
  }

  private static renderTitleSequenceFields(readonly = false) {
    return (
      <FormSection name="titleSequence">
        <AppTextField name="background" label="Background" validate={required} disabled={readonly} />
        <AppTextField
          name="clipLength"
          label="Clip length"
          validate={[numberFormat, required]}
          type="number"
          disabled={readonly}
        />
      </FormSection>
    );
  }

  private static renderOverlaysFields(name: string, component: any) {
    return <FieldArray name={name} component={component} />;
  }

  private static renderWarningsFields(readonly = false) {
    return (
      <FormSection name="warnings">
        <AppTextField
          name="minimumVideoLength"
          label="Minimum video length"
          validate={numberFormat}
          type="number"
          disabled={readonly}
        />
        <AppTextField
          name="maximumVideoLength"
          label="Maximum video length"
          validate={numberFormat}
          type="number"
          disabled={readonly}
        />
        <AppTextField
          name="maximumNumberOfCharactersInName"
          label="Maximum number of letters in name"
          validate={numberFormat}
          type="number"
          disabled={readonly}
        />
        <AppTextField
          name="maximumNumberOfCharactersInSubtitle"
          label="Maximum number of letters in subtitle"
          validate={numberFormat}
          type="number"
          disabled={readonly}
        />
      </FormSection>
    );
  }

  private static renderAudioNormalizationFields(readonly = false) {
    const orderOptions = [AudioNormalizationOrder.FinalVideo, AudioNormalizationOrder.ClipVideo].map((type: any) => ({
      value: type,
      label: type,
    }));

    return (
      <FormSection name="audioNormalization">
        <AppSelectField
          name="order"
          label="Order"
          options={orderOptions}
          className={styles.groupedProp}
          disabled={readonly}
          defaultKey={DEFAULT_AUDIO_NORMALIZATION_ORDER}
        />
      </FormSection>
    );
  }

  private readonly overlaysComponent: any;

  private readonly creditOverlaysComponent: any;

  constructor(props: any) {
    super(props);
    this.overlaysComponent = this.renderOverlays(['overlays'], props.readonly).bind(this);
    this.creditOverlaysComponent = this.renderOverlays(['creditSequence', 'overlays'], props.readonly).bind(this);
    this.state = {
      creditsOverlaysSelectedIndex: 0,
      interstitialsSelectedIndex: 0,
      overlaysSelectedIndex: 0,
    };
  }

  protected loggedInRender() {
    const { readonly, customStyles } = this.props;
    const tabConfigStyles = customStyles ? customStyles.tabConfig : undefined;
    const containerStyles = customStyles ? customStyles.container : undefined;

    return (
      <div className={`${styles.clipConfigurationWrapper} `}>
        <Collapsible trigger={this.collapsibleTrigger()}>
          <FormSection name="clipConfiguration">
            <Tabs className={classnames(styles.configTabs, containerStyles)} selectedTabClassName={styles.selectedTab}>
              <TabList className={classnames(styles.configTabList, tabConfigStyles)}>
                {this.renderTabName('audio', 'Audio')}
                {this.renderTabName('blobStorage', 'Blob Storage')}
                {this.renderTabName('clip', 'Clip')}
                {this.renderTabName('titleSequence', 'Title Sequence')}
                {this.renderTabName('creditSequence', 'Credit Sequence')}
                {this.renderTabName('overlays', 'Overlays')}
                {this.renderTabName('interstitials', 'Interstitials')}
                {this.renderTabName('warnings', 'Warnings')}
                {this.renderTabName('audioNormalization', 'Audio normalization')}
              </TabList>
              <TabPanel className={styles.configTabPanel} forceRender>
                {ClipConfigurationForm.renderAudioFields(readonly)}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {ClipConfigurationForm.renderBlobStorageFields(readonly)}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {ClipConfigurationForm.renderClipFields(readonly)}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {ClipConfigurationForm.renderTitleSequenceFields(readonly)}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {this.renderCreditSequenceFields(readonly)}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {ClipConfigurationForm.renderOverlaysFields('overlays', this.overlaysComponent)}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {React.cloneElement(<FieldArray name="interstitials" component={this.renderInterstitials} />, {
                  readonly,
                })}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {ClipConfigurationForm.renderWarningsFields(readonly)}
              </TabPanel>
              <TabPanel className={styles.configTabPanel} forceRender>
                {ClipConfigurationForm.renderAudioNormalizationFields(readonly)}
              </TabPanel>
            </Tabs>
          </FormSection>
        </Collapsible>
      </div>
    );
  }

  private renderTabName(fieldName: string, displayName: string) {
    return (
      <Tab className={styles.configTab}>
        {displayName}
        {this.props.clipConfigurationFormErrors && this.props.clipConfigurationFormErrors[fieldName] && (
          <span className={styles.error}>Invalid</span>
        )}
      </Tab>
    );
  }

  private collapsibleTrigger() {
    return (
      <span>
        Clip Configuration
        {this.props.clipConfigurationFormErrors && (
          <span className={styles.error}>
            Invalid clip configuration. Please check if all the fields are correctly filled
          </span>
        )}
      </span>
    );
  }

  private displayCustomOverlayProps(index: any, overlay: any, path: string[], readonly = false) {
    const component = this.props.formValues
      ? path.reduce((acc: any, c: any) => acc[c], this.props.formValues.clipConfiguration)
      : undefined;
    const animationOptions = ['cut', 'fade'].map((type: any) => ({ value: type, label: type }));

    let timestampsAndAnimations;

    if (path[0] !== 'creditSequence') {
      timestampsAndAnimations = (
        <>
          <div className={styles.groupedProps}>
            <AppSelectField
              name={`${overlay}.startWith`}
              label="Start with"
              options={animationOptions}
              className={styles.groupedProp}
              validate={required}
              disabled={readonly}
            />
            <AppSelectField
              name={`${overlay}.endWith`}
              label="End with"
              options={animationOptions}
              className={styles.groupedProp}
              validate={required}
              disabled={readonly}
            />
          </div>

          <div className={styles.groupedProps}>
            <AppTextField
              name={`${overlay}.startTimestamp`}
              label="Start timestamp"
              className={styles.groupedProp}
              validate={required}
              disabled={readonly}
            />
            <AppTextField
              name={`${overlay}.endTimestamp`}
              label="End timestamp"
              className={styles.groupedProp}
              validate={required}
              disabled={readonly}
            />
          </div>
        </>
      );
    }

    if (component && component[index].type === 'text') {
      return (
        <>
          {timestampsAndAnimations}
          <div className={styles.groupedProps}>
            <AppTextField
              name={`${overlay}.color`}
              label="Color"
              className={styles.groupedProp}
              validate={required}
              disabled={readonly}
            />
            <AppTextField
              name={`${overlay}.size`}
              label="Font size"
              validate={[numberFormat, required]}
              className={styles.groupedProp}
              type="number"
              disabled={readonly}
            />
          </div>
          <AppTextField
            name={`${overlay}.font`}
            label="Font url"
            labelClassName={styles.labelWrapper}
            inputClassName={styles.inputWrapper}
            disabled={readonly}
          />
          <AppTextField
            name={`${overlay}.content`}
            label="Content"
            labelClassName={styles.labelWrapper}
            validate={required}
            inputClassName={styles.inputWrapper}
            disabled={readonly}
          />{' '}
        </>
      );
    }
    let videoFields;

    if (component && component[index].type === 'video') {
      videoFields = (
        <>
          <AppTextField
            name={`${overlay}.colorkey.color`}
            label="Colorkey color"
            labelClassName={styles.labelWrapper}
            inputClassName={styles.inputWrapper}
            disabled={readonly}
          />
          <div className={styles.groupedProps}>
            <AppTextField
              name={`${overlay}.colorkey.similarity`}
              label="Colorkey similarity"
              validate={[numberFormat]}
              className={styles.groupedProp}
              disabled={readonly}
            />
            <AppTextField
              name={`${overlay}.colorkey.blend`}
              label="Colorkey blend"
              validate={[numberFormat]}
              className={styles.groupedProp}
              disabled={readonly}
            />
          </div>
        </>
      );
    }

    return (
      <>
        {timestampsAndAnimations}
        <AppTextField
          name={`${overlay}.source`}
          label="Source"
          labelClassName={styles.labelWrapper}
          validate={required}
          inputClassName={styles.inputWrapper}
          disabled={readonly}
        />
        <div className={styles.groupedProps}>
          <AppTextField
            name={`${overlay}.height`}
            label="Height"
            validate={[numberFormat, required]}
            className={styles.groupedProp}
            type="number"
            disabled={readonly}
          />
          <AppTextField
            name={`${overlay}.width`}
            label="Width"
            validate={[numberFormat, required]}
            className={styles.groupedProp}
            type="number"
            disabled={readonly}
          />
        </div>
        {videoFields}
      </>
    );
  }

  private renderCreditSequenceFields(readonly = false) {
    return (
      <FormSection name="creditSequence">
        <AppTextField name="background" label="Background" validate={required} disabled={readonly} />
        <AppTextField
          name="clipLength"
          label="Clip length"
          validate={[numberFormat, required]}
          type="number"
          disabled={readonly}
        />
        {ClipConfigurationForm.renderOverlaysFields('overlays', this.creditOverlaysComponent)}
      </FormSection>
    );
  }

  private onOverlayTypeChangeCallBack = (overlay: any) => (value: any) => {
    // Reset fields' values
    if (value.value === 'text') {
      this.props.changeFieldValue(`${overlay}.source`, null);
      this.props.changeFieldValue(`${overlay}.height`, null);
      this.props.changeFieldValue(`${overlay}.width`, null);
    } else {
      this.props.changeFieldValue(`${overlay}.color`, null);
      this.props.changeFieldValue(`${overlay}.size`, null);
      this.props.changeFieldValue(`${overlay}.content`, null);
      this.props.changeFieldValue(`${overlay}.font`, null);
    }

    if (value.value !== 'video') {
      this.props.changeFieldValue(`${overlay}.colorkey.color`, null);
      this.props.changeFieldValue(`${overlay}.colorkey.similarity`, null);
      this.props.changeFieldValue(`${overlay}.colorkey.blend`, null);
    }
  };

  private renderInterstitials = ({ fields, readonly = false }: any) => {
    const beforeOptions = ['titles', 'credits'].map((option: any) => ({ value: option, label: option }));
    const errors = this.props.clipConfigurationFormErrors
      ? this.props.clipConfigurationFormErrors.interstitials
      : undefined;
    const subFields = (interstitial: any) => (
      <>
        <AppTextField name={`${interstitial}.name`} label="Name" className={styles.groupedProps} disabled={readonly} />
        <AppTextField
          name={`${interstitial}.source`}
          label="Source"
          className={styles.groupedProps}
          validate={required}
          disabled={readonly}
        />
        <div className={styles.groupedProps}>
          <AppSelectField
            name={`${interstitial}.before`}
            options={beforeOptions}
            label="Before"
            className={styles.groupedProp}
            validate={required}
            disabled={readonly}
          />
          <AppTextField
            name={`${interstitial}.volume`}
            label="Volume"
            className={styles.groupedProp}
            validate={[required, numberFormat]}
            type="number"
            disabled={readonly}
          />
        </div>
      </>
    );

    return (
      <EditableFieldArray
        fields={fields}
        newField={() => ({})}
        indexName="interstitialsSelectedIndex"
        fieldName="Interstitial"
        errors={errors}
        subFields={subFields}
        readonly={readonly}
      />
    );
  };

  private renderOverlays = (path: string[], readonly = false) => ({ fields }: any) => {
    const indexName = path[0] === 'creditSequence' ? 'creditsOverlaysSelectedIndex' : 'overlaysSelectedIndex';
    const typeOptions = ['graphic', 'text', 'video'].map((type: any) => ({ value: type, label: type }));
    const overlayErrors = path.reduce(
      (acc: any, c: any) => (acc ? acc[c] : acc),
      this.props.clipConfigurationFormErrors,
    );
    const subFields = (overlay: any, index: any) => {
      const fieldName = `${path.join('.')}[${index}]`;
      const onTypeChangeCallback = this.onOverlayTypeChangeCallBack(fieldName).bind(this);

      return (
        <>
          <AppTextField name={`${overlay}.name`} label="Name" className={styles.groupedProp} disabled={readonly} />
          <AppSelectField
            name={`${overlay}.type`}
            label="Type"
            options={typeOptions}
            className={styles.groupedProp}
            validate={required}
            onChangeCallback={onTypeChangeCallback}
            disabled={readonly}
          />
          <div className={styles.groupedProps}>
            <AppTextField
              name={`${overlay}.x`}
              label="Horizontal position"
              className={styles.groupedProp}
              validate={required}
              disabled={readonly}
            />
            <AppTextField
              name={`${overlay}.y`}
              label="Vertical position"
              className={styles.groupedProp}
              validate={required}
              disabled={readonly}
            />
          </div>
          {this.displayCustomOverlayProps(index, overlay, path, readonly)}
        </>
      );
    };

    return (
      <EditableFieldArray
        fields={fields}
        newField={() => ({})}
        indexName={indexName}
        fieldName="Overlay"
        errors={overlayErrors}
        subFields={subFields}
        readonly={readonly}
      />
    );
  };
}

function mapStateToProps(
  state: ClippingPlatformState,
  ownProps: ClipConfigurationOwnProps,
): ClipConfigurationStateProps {
  const formData = state.form[ownProps.formId];

  return {
    ...baseMapStateToProps(state),
    clipConfigurationFormErrors: formData
      ? ((formData as any).syncErrors && (formData as any).syncErrors.clipConfiguration) ||
        !formData.values.clipConfiguration
      : undefined,
    formValues: formData ? formData.values : undefined,
  };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: ClipConfigurationOwnProps): ClipConfigurationDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    changeFieldValue: (field, value) => dispatch(change(ownProps.formId, `clipConfiguration.${field}`, value)),
    clearSyncErrors: () => dispatch(createClearSyncErrorsAction(ownProps.formId)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ClipConfigurationForm);
